import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Search, Bell, Sparkles, Home, FileText, ShieldCheck,
    UserRound, BarChart3, SlidersHorizontal,
    MessageSquare, Inbox, Menu, X, LogIn, FolderClosed, Euro
} from 'lucide-react';
import ThemeToggle from '../ui/ThemeToggle';
import api from '../../lib/api';

/**
 * ReseauLayout — Shell unifié pour toutes les pages réseau (publiques et
 * cockpit reliées au réseau).
 *
 * Principes (brief fondateur) :
 * - "Mon activité" (cockpit) et "Mon réseau et ma visibilité" (réseau) sont
 *   visuellement reliés, jamais opposés.
 * - Header mobile-first, breadcrumb implicite via titre.
 * - Aucune surcharge violette : couleurs sobres, accents ponctuels.
 * - Le slot `headerTitle` permet d'afficher le contexte de la page courante.
 *
 * Usage :
 *   <ReseauLayout user={user} headerTitle="Fil local">
 *     ...contenu...
 *   </ReseauLayout>
 */

const RESEAU_NAV = [
    { to: '/reseau/cockpit', label: 'Cockpit', icon: Sparkles, testid: 'rnav-cockpit' },
    { to: '/reseau/fil', label: 'Fil', icon: Home, testid: 'rnav-feed' },
    { to: '/reseau/mes-publications', label: 'Publications', icon: FileText, testid: 'rnav-my-posts' },
    { to: '/reseau/ma-reputation', label: 'Réputation', icon: ShieldCheck, testid: 'rnav-my-proofs' },
    { to: '/reseau/avis-vendeurs', label: 'Avis', icon: MessageSquare, testid: 'rnav-seller-reviews' },
    { to: '/reseau/demandes-vendeurs', label: 'Demandes', icon: Inbox, testid: 'rnav-seller-intents' },
    { to: '/notaire/dossiers', label: 'Notaire', icon: FolderClosed, testid: 'rnav-notary', proOnly: true },
    { to: '/agence/commissions', label: 'Commissions', icon: Euro, testid: 'rnav-commissions', proOnly: true },
    { to: '/reseau/profil', label: 'Mon CV', icon: UserRound, testid: 'rnav-profil' },
    { to: '/reseau/statistiques', label: 'Stats', icon: BarChart3, testid: 'rnav-stats' },
    { to: '/reseau/parametres', label: 'Paramètres', icon: SlidersHorizontal, testid: 'rnav-settings' },
];

function HomlyMark({ withSubtitle = true, user = null }) {
    const isPro = user && ['agent', 'admin', 'super_admin', 'internal', 'group_manager'].includes(user.role);
    const target = isPro
        ? (user.role === 'admin' ? '/admin' : user.role === 'agent' ? '/agent' : '/dashboard')
        : '/reseau';
    return (
        <Link to={target} className="flex items-center gap-2 group" data-testid="reseau-logo">
            <span
                className="text-2xl font-extrabold tracking-tight"
                style={{ fontFamily: 'var(--reseau-font-display)' }}
            >
                <span className="reseau-gradient-text">Homly</span>
                <span style={{ color: 'var(--reseau-orange)' }}>.</span>
            </span>
            {withSubtitle && (
                <span
                    className="hidden sm:inline text-sm font-medium"
                    style={{ color: 'var(--reseau-ink-muted)' }}
                >
                    Réseau
                </span>
            )}
        </Link>
    );
}

function NotificationBell({ user }) {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!user) return;
        let active = true;
        const tick = () => {
            api.get('/reseau/notifications/unread-count')
                .then(r => active && setCount(r.data?.count || 0))
                .catch(() => {});
        };
        tick();
        const id = setInterval(tick, 45000);
        return () => { active = false; clearInterval(id); };
    }, [user]);
    return (
        <Link
            to="/reseau/notifications"
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full reseau-btn-ghost"
            style={{ padding: 0 }}
            aria-label="Notifications"
            data-testid="rnav-notifications"
        >
            <Bell className="w-4 h-4" />
            {count > 0 && (
                <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold"
                    style={{ background: 'var(--reseau-orange)', color: '#fff' }}
                    data-testid="rnav-notif-count"
                >
                    {count > 9 ? '9+' : count}
                </span>
            )}
        </Link>
    );
}

function ContextSwitcher({ user, location }) {
    // "Mon activité" (cockpit) vs "Mon réseau" — relié, jamais opposé
    const inReseau = location.pathname.startsWith('/reseau') || location.pathname.startsWith('/p/');
    if (!user) return null;
    const cockpitTarget = user.role === 'admin' ? '/admin' : user.role === 'agent' ? '/agent' : '/dashboard';
    return (
        <div
            className="hidden md:inline-flex items-center gap-1 p-1 rounded-full"
            style={{ background: 'var(--reseau-bg-soft)', border: '1px solid var(--reseau-border)' }}
            data-testid="reseau-context-switcher"
        >
            <Link
                to={cockpitTarget}
                className="px-3 py-1.5 rounded-full text-sm font-semibold transition-colors"
                style={{
                    background: !inReseau ? 'var(--reseau-surface)' : 'transparent',
                    color: !inReseau ? 'var(--reseau-ink)' : 'var(--reseau-ink-muted)',
                    boxShadow: !inReseau ? 'var(--reseau-shadow-xs)' : 'none',
                }}
                data-testid="ctx-mon-activite"
            >
                Mon activité
            </Link>
            <Link
                to="/reseau/fil"
                className="px-3 py-1.5 rounded-full text-sm font-semibold transition-colors"
                style={{
                    background: inReseau ? 'var(--reseau-surface)' : 'transparent',
                    color: inReseau ? 'var(--reseau-ink)' : 'var(--reseau-ink-muted)',
                    boxShadow: inReseau ? 'var(--reseau-shadow-xs)' : 'none',
                }}
                data-testid="ctx-mon-reseau"
            >
                Mon réseau
            </Link>
        </div>
    );
}

export default function ReseauLayout({ user: userProp, children, headerTitle, headerSubtitle, hideNav = false }) {
    const location = useLocation();
    const [user, setUser] = useState(userProp || null);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        if (userProp !== undefined && userProp !== null) {
            setUser(userProp);
            return;
        }
        api.get('/auth/me').then(r => setUser(r.data)).catch(() => setUser(null));
    }, [userProp]);

    const isPro = user && ['agent', 'admin', 'super_admin', 'internal', 'group_manager'].includes(user.role);

    return (
        <div
            data-reseau="light"
            className="reseau-light min-h-screen"
            style={{ background: 'var(--reseau-bg)' }}
        >
            {/* ── Header ─────────────────────────────────────────────── */}
            <header
                className="sticky top-0 z-30"
                style={{
                    background: 'var(--reseau-header-bg, rgba(250, 250, 247, 0.92))',
                    backdropFilter: 'blur(12px)',
                    borderBottom: '1px solid var(--reseau-border)',
                }}
                data-testid="reseau-header"
            >
                <div className="mx-auto px-4 sm:px-6" style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                    <div className="flex items-center justify-between h-16">
                        {/* Gauche : logo + bascule */}
                        <div className="flex items-center gap-4">
                            <HomlyMark user={user} />
                            <ContextSwitcher user={user} location={location} />
                        </div>

                        {/* Droite : recherche + theme + notifs + user */}
                        <div className="flex items-center gap-2">
                            <Link
                                to="/reseau/recherche"
                                className="hidden sm:inline-flex reseau-btn reseau-btn-ghost"
                                style={{ padding: '8px 12px' }}
                                data-testid="rnav-search"
                            >
                                <Search className="w-4 h-4" />
                                <span>Rechercher</span>
                            </Link>
                            <ThemeToggle showLabels={false} size="sm" />
                            {isPro && <NotificationBell user={user} />}
                            {!user && (
                                <Link
                                    to="/login"
                                    className="reseau-btn reseau-btn-primary"
                                    data-testid="rnav-login"
                                >
                                    <LogIn className="w-4 h-4" />
                                    Connexion
                                </Link>
                            )}
                            {user && (
                                <Link
                                    to={user.role === 'admin' ? '/admin/profile' : '/agent/profile'}
                                    className="reseau-btn reseau-btn-ghost"
                                    style={{ padding: '8px 12px' }}
                                    data-testid="rnav-profile"
                                >
                                    {user.first_name?.[0] || 'M'}{user.last_name?.[0] || ''}
                                </Link>
                            )}
                            <button
                                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full reseau-btn-ghost"
                                style={{ padding: 0 }}
                                onClick={() => setMobileOpen(v => !v)}
                                aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                                data-testid="reseau-mobile-toggle"
                            >
                                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    {/* Navigation secondaire desktop */}
                    {!hideNav && isPro && (
                        <nav
                            className="hidden md:flex items-center gap-1 pb-3 -mt-1 overflow-x-auto scrollbar-hide"
                            data-testid="reseau-nav-desktop"
                            style={{ scrollbarWidth: 'none' }}
                        >
                            {RESEAU_NAV.map(item => {
                                const active = location.pathname.startsWith(item.to);
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.to}
                                        to={item.to}
                                        data-testid={item.testid}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0"
                                        style={{
                                            color: active ? 'var(--reseau-violet)' : 'var(--reseau-ink-soft)',
                                            background: active ? 'var(--reseau-violet-soft)' : 'transparent',
                                        }}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {item.label}
                                    </Link>
                                );
                            })}
                        </nav>
                    )}
                </div>

                {/* Drawer mobile */}
                {mobileOpen && (
                    <div
                        className="md:hidden border-t"
                        style={{ background: 'var(--reseau-surface)', borderColor: 'var(--reseau-border)' }}
                        data-testid="reseau-mobile-drawer"
                    >
                        <div className="px-4 py-3 space-y-1">
                            {isPro && (
                                <>
                                    <Link
                                        to={user.role === 'admin' ? '/admin' : user.role === 'agent' ? '/agent' : '/dashboard'}
                                        onClick={() => setMobileOpen(false)}
                                        className="flex items-center gap-3 px-3 py-3 rounded-lg"
                                        style={{ color: 'var(--reseau-violet)', fontWeight: 600 }}
                                    >
                                        <Sparkles className="w-4 h-4" /> Mon activité (cockpit)
                                    </Link>
                                    {RESEAU_NAV.map(item => {
                                        const Icon = item.icon;
                                        return (
                                            <Link
                                                key={item.to}
                                                to={item.to}
                                                onClick={() => setMobileOpen(false)}
                                                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm"
                                                style={{ color: 'var(--reseau-ink-soft)' }}
                                                data-testid={`mobile-${item.testid}`}
                                            >
                                                <Icon className="w-4 h-4" /> {item.label}
                                            </Link>
                                        );
                                    })}
                                </>
                            )}
                            <Link
                                to="/reseau/recherche"
                                onClick={() => setMobileOpen(false)}
                                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm"
                                style={{ color: 'var(--reseau-ink-soft)' }}
                            >
                                <Search className="w-4 h-4" /> Rechercher
                            </Link>
                        </div>
                    </div>
                )}
            </header>

            {/* Titre de page contextuel */}
            {headerTitle && (
                <div
                    className="border-b"
                    style={{ background: 'var(--reseau-bg-soft)', borderColor: 'var(--reseau-border)' }}
                >
                    <div
                        className="mx-auto px-4 sm:px-6 py-4 sm:py-6"
                        style={{ maxWidth: 'var(--reseau-maxw-page)' }}
                    >
                        <h1
                            className="text-2xl sm:text-3xl font-bold reseau-h1"
                            style={{ fontFamily: 'var(--reseau-font-display)' }}
                            data-testid="reseau-page-title"
                        >
                            {headerTitle}
                        </h1>
                        {headerSubtitle && (
                            <p
                                className="mt-1 text-sm sm:text-base"
                                style={{ color: 'var(--reseau-ink-muted)' }}
                                data-testid="reseau-page-subtitle"
                            >
                                {headerSubtitle}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Contenu principal */}
            <main data-testid="reseau-main">{children}</main>

            {/* Footer minimal */}
            <footer
                className="mt-12 border-t"
                style={{ borderColor: 'var(--reseau-border)', background: 'var(--reseau-surface)' }}
            >
                <div
                    className="mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm"
                    style={{ maxWidth: 'var(--reseau-maxw-page)', color: 'var(--reseau-ink-muted)' }}
                >
                    <HomlyMark withSubtitle={false} />
                    <div className="flex items-center gap-4">
                        <Link to="/protection-des-donnees" className="hover:underline">Mes données</Link>
                        <Link to="/cgu" className="hover:underline">CGU</Link>
                        <Link to="/privacy" className="hover:underline">Confidentialité</Link>
                        <Link to="/" className="hover:underline">Site principal</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
